import React, { useState } from "react";
import { XIcon } from "lucide-react";
import FilterIcon from "@/components/ui/filtericon";
import RangeSlider from "@/components/ui/range-slider";

interface FilterComponentProps {
  isFilterOpen: boolean;
  setIsFilterOpen: (isOpen: boolean) => void;
  priceRange: [number, number];
  availability: {
    inStock: boolean;
    outOfStock: boolean;
  };
  inStockCount: number;
  outOfStockCount: number;
  onFilterChange: (filters: {
    priceRange: [number, number];
    availability: {
      inStock: boolean;
      outOfStock: boolean;
    };
  }) => void;
}

export default function FilterComponent({
  isFilterOpen,
  setIsFilterOpen,
  priceRange: initialPriceRange,
  availability: initialAvailability,
  inStockCount,
  outOfStockCount,
  onFilterChange,
}: FilterComponentProps) {
  // Local state for filter values
  const [localPriceRange, setLocalPriceRange] =
    useState<[number, number]>(initialPriceRange);
  const [localAvailability, setLocalAvailability] =
    useState(initialAvailability);

  // Handle price range change
  const handlePriceRangeChange = (newRange: [number, number]) => {
    setLocalPriceRange(newRange);
  };

  // Handle availability toggle - make it mutually exclusive like radio buttons
  const handleAvailabilityToggle = (key: "inStock" | "outOfStock") => {
    if (key === "inStock") {
      setLocalAvailability({
        inStock: true,
        outOfStock: false,
      });
    } else {
      setLocalAvailability({
        inStock: false,
        outOfStock: true,
      });
    }
  };

  // Apply filters
  const applyFilters = () => {
    onFilterChange({
      priceRange: localPriceRange,
      availability: localAvailability,
    });
    setIsFilterOpen(false);
  };

  return (
    <>
      {/* Filter Button */}
      <button
        id="filter-button"
        aria-label="Open filter panel"
        className="flex items-center justify-center gap-2 border border-gray-300 px-3 py-2 text-sm w-1/2 md:w-auto"
        onClick={() => setIsFilterOpen(true)}
      >
        <FilterIcon />
        <span className="font-['Ainslie_Sans'] font-[600] text-[#1C1D1D] text-[16px]">
          Filter
        </span>
      </button>

      {/* Filter panel with responsive width */}
      {isFilterOpen && (
        <>
          {/* Filter Sidebar */}
          <div className="fixed left-0 top-0 h-full w-3/4 max-w-xs bg-white shadow-lg p-4 sm:p-6 z-50 overflow-y-auto">
            <div className="relative flex items-center mb-4">
              {/* Centered Title */}
              <h3 className="font-['Ainslie_Sans'] font-[600] text-[#000000] text-xl absolute left-1/2 transform -translate-x-1/2">
                FILTER
              </h3>

              {/* Close Button */}
              <button
                id="close-filter-button"
                aria-label="Close filter panel"
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="ml-auto p-1"
              >
                <XIcon size={24} />
              </button>
            </div>

            {/* Filter Sections */}
            <div className="space-y-6">
              {/* Availability Dropdown */}
              <div className="border-b pb-4">
                <details open className="group">
                  <summary className="flex justify-between items-center cursor-pointer py-2">
                    <span className="text-lg  font-['Ainslie_Sans'] font-[600] text-[#000000] ">
                      Availability
                    </span>
                    <svg
                      className="w-5 h-5 group-open:rotate-180 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>

                  {/* Availability Options */}
                  <div className="mt-4 font-['Ainslie_Sans'] font-[500] space-y-2">
                    <div
                      className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                        localAvailability.inStock
                          ? "bg-red-100 border border-red-300"
                          : ""
                      }`}
                      onClick={() => handleAvailabilityToggle("inStock")}
                    >
                      <span
                        className={
                          localAvailability.inStock
                            ? "font-semibold text-red-700"
                            : ""
                        }
                      >
                        In Stock
                      </span>
                      <span>({inStockCount})</span>
                    </div>
                    <div
                      className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                        localAvailability.outOfStock
                          ? "bg-red-100 border border-red-300"
                          : ""
                      }`}
                      onClick={() => handleAvailabilityToggle("outOfStock")}
                    >
                      <span
                        className={
                          localAvailability.outOfStock
                            ? "font-semibold text-red-700"
                            : ""
                        }
                      >
                        Out of Stock
                      </span>
                      <span>({outOfStockCount})</span>
                    </div>
                  </div>
                </details>
              </div>

              {/* Price Dropdown */}
              <div className="border-b pb-4">
                <details open className="group">
                  <summary className="flex justify-between items-center cursor-pointer py-2">
                    <span className="text-lg  font-['Ainslie_Sans'] font-[600] text-[#000000]">
                      Price
                    </span>
                    <svg
                      className="w-5 h-5 group-open:rotate-180 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>

                  {/* Price Range Slider */}
                  <div className="mt-4">
                    <div className="flex justify-between  font-['Ainslie_Sans'] font-[500] text-[18px] font-Poppins mb-2">
                      <span>Rs. {localPriceRange[0]}</span>
                      <span>Rs. {localPriceRange[1]}</span>
                    </div>

                    <RangeSlider
                      min={0}
                      max={1399}
                      value={localPriceRange}
                      onChange={handlePriceRangeChange}
                      className="mt-4"
                    />
                  </div>
                </details>
              </div>
            </div>

            {/* Apply Filters Button */}
            <div className="mt-6">
              <button
                id="apply-filters-button"
                aria-label="Apply filters"
                type="button"
                onClick={applyFilters}
                className="w-full  font-['Ainslie_Sans'] font-[600]  bg-black text-white py-2 px-4 rounded"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsFilterOpen(false)}
          ></div>
        </>
      )}
    </>
  );
}
